"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { ArrowUpRight, Minus, Play } from "lucide-react";
import { cn } from "@/utils/cn";

export interface WellnessCardItem {
  id: string;
  tag: string;
  title: string;
  subtext: string;
  image?: string;
  videoUrl?: string;
  mediaTag?: string;
  video?: {
    duration: string;
    title: string;
    url?: string;
  };
  active?: boolean;
}

export interface WellnessStatItem {
  value: string;
  label: string;
}

export interface FamilyWellnessData {
  left: {
    image: string;
    tag: string;
    video?: {
      duration: string;
      title: string;
      url?: string;
    };
    stats?: WellnessStatItem[];
    disclaimer?: string;
  };
  right: {
    sectionIndex?: string;
    totalSections?: string;
    tag: string;
    title: string;
    description?: string;
    cards: WellnessCardItem[];
  };
}

interface FamilyWellnessSectionProps {
  data: FamilyWellnessData;
  onCardSelect?: (cardId: string) => void;
  onVideoClick?: () => void;
}

const getAutoplayUrl = (url: string) => {
  if (!url) return "";
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    const videoIdMatch = url.match(
      /(?:embed\/|v=|v\/|vi\/|youtu\.be\/|\/v\/)([^#&?]*)/,
    );
    const videoId = videoIdMatch ? videoIdMatch[1] : "";
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&playsinline=1&enablejsapi=1`;
    }
  }
  return url;
};

export default function FamilyWellnessSection({
  data,
  onCardSelect,
  onVideoClick,
}: FamilyWellnessSectionProps) {
  const [activeCardId, setActiveCardId] = useState<string>(
    data?.right?.cards?.[0]?.id || "01",
  );

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const videoRef = useRef<HTMLVideoElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const userPausedRef = useRef(false);
  const userMutedRef = useRef(false);
  const hasAutoUnmutedRef = useRef(false);

  if (!data) return null;

  const { left, right } = data;

  const handleCardClick = (id: string) => {
    setActiveCardId(id);
    if (onCardSelect) {
      onCardSelect(id);
    }
  };

  const activeCard =
    right?.cards?.find((c) => c.id === activeCardId) || right?.cards?.[0];

  const activeImage =
    activeCard?.image || left?.image || "/images/phoi_canh_2.png";
  const activeVideoUrl = activeCard?.videoUrl || activeCard?.video?.url || "";
  const activeMediaTag = activeCard?.mediaTag || left?.tag || "";
  const activeVideoInfo = activeCard?.video || left?.video;
  const hasVideoUrl = Boolean(activeVideoUrl && activeVideoUrl.trim() !== "");

  const isYouTube =
    activeVideoUrl.includes("youtube.com") ||
    activeVideoUrl.includes("youtu.be");

  const sendYouTubeCommand = (func: string, args: any[] = []) => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: func,
          args: args,
        }),
        "*",
      );
    }
  };

  const pauseVideo = () => {
    setIsPlaying(false);
    if (!isYouTube) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    } else {
      sendYouTubeCommand("pauseVideo", []);
    }
  };

  const playVideo = () => {
    setIsPlaying(true);
    if (!isYouTube) {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
    } else {
      sendYouTubeCommand("playVideo", []);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      userPausedRef.current = true;
      pauseVideo();
    } else {
      userPausedRef.current = false;
      playVideo();
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    const shouldMute = newVolume === 0;
    setIsMuted(shouldMute);
    userMutedRef.current = shouldMute;

    if (!isYouTube) {
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = shouldMute;
      }
    } else {
      sendYouTubeCommand("setVolume", [Math.round(newVolume * 100)]);
      if (shouldMute) {
        sendYouTubeCommand("mute", []);
      } else {
        sendYouTubeCommand("unMute", []);
      }
    }
  };

  const toggleMute = () => {
    const nextMutedState = !isMuted;
    setIsMuted(nextMutedState);
    userMutedRef.current = nextMutedState;

    if (nextMutedState) {
      if (!isYouTube) {
        if (videoRef.current) videoRef.current.muted = true;
      } else {
        sendYouTubeCommand("mute", []);
      }
    } else {
      const activeVolume = volume === 0 ? 0.5 : volume;
      if (volume === 0) setVolume(0.5);

      if (!isYouTube) {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = activeVolume;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(activeVolume * 100)]);
      }
    }
  };

  // Pause video when section scrolls out of view, auto unmute at 50% volume on first scroll into view (unless user muted)
  useEffect(() => {
    if (!hasVideoUrl) return;

    const applyAutoUnmute = () => {
      if (userMutedRef.current) return;

      const targetVol = volume > 0 ? volume : 0.5;
      setVolume(targetVol);
      setIsMuted(false);

      if (!isYouTube) {
        if (videoRef.current) {
          videoRef.current.volume = targetVol;
          videoRef.current.muted = false;
        }
      } else {
        sendYouTubeCommand("unMute", []);
        sendYouTubeCommand("setVolume", [Math.round(targetVol * 100)]);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          pauseVideo();
        } else {
          if (!userPausedRef.current) {
            playVideo();
          }
          if (!hasAutoUnmutedRef.current) {
            hasAutoUnmutedRef.current = true;
            applyAutoUnmute();
          } else if (!userMutedRef.current) {
            applyAutoUnmute();
          }
        }
      },
      { threshold: 0.15 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    const handleFirstGesture = () => {
      if (!userMutedRef.current) {
        applyAutoUnmute();
      }
    };

    window.addEventListener("pointerdown", handleFirstGesture, { once: true });
    window.addEventListener("keydown", handleFirstGesture, { once: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("pointerdown", handleFirstGesture);
      window.removeEventListener("keydown", handleFirstGesture);
    };
  }, [hasVideoUrl, isYouTube, volume]);

  return (
    <section
      id="gia-dinh-song-khoe"
      ref={sectionRef}
      className="relative w-full min-h-screen lg:h-screen flex flex-col lg:flex-row overflow-hidden bg-primaryDark text-white select-none"
    >
      {/* ================= LEFT COLUMN: CARDS ACCORDION (35% WIDTH ON DESKTOP, FIRST ON MOBILE) ================= */}
      <div className="w-full lg:w-[35%] min-h-[auto] lg:h-full text-white flex flex-col justify-center px-6 sm:px-8 lg:px-10 xl:px-12 pt-12 sm:pt-16 lg:pt-22 pb-8 lg:pb-10 relative overflow-y-auto lg:overflow-hidden order-1 lg:order-1 flex-shrink-0">
        {/* BACKGROUND GRADIENT IN TAILWIND PRIMARY & PRIMARY DARK PALETTE */}
        <div className="absolute inset-0 bg-gradient-to-br from-primaryDark via-[#085C47] to-primary pointer-events-none z-0 opacity-95" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/25 rounded-full blur-3xl pointer-events-none" />

        {/* DECORATIVE SHADOW OVERLAY IMAGE (FULL HEIGHT) */}
        <div className="absolute inset-y-0 left-0 w-full h-full pointer-events-none z-[1] opacity-100 mix-blend-overlay">
          <Image
            src="/images/deco_shadow_left_bottom.png"
            alt="Decorative Shadow Overlay"
            fill
            sizes="(max-width: 1024px) 100vw, 35vw"
            priority
            className="object-cover object-left-bottom"
          />
        </div>

        {/* MAIN CONTAINER ANCHORED FROM BOTTOM */}
        <div className="relative z-10 flex flex-col space-y-3 sm:space-y-4">
          {/* HEADER ROW: TAG & COUNTER & TITLE & SHORT DESC */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-3">
                <span className="w-6 h-[1px] bg-white/40" />
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-white/70 font-sans">
                  {right.tag}
                </span>
              </div>
            </div>

            {/* MAIN HEADING */}
            <h2
              className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl font-lora font-normal text-white tracking-tight mb-1.5 title-leading"
              style={{ lineHeight: 1.15 }}
            >
              {renderTextWithBreaks(right.title)}
            </h2>

            {/* SHORT DESCRIPTION */}
            {right.description && (
              <p className="text-xs sm:text-sm font-light text-white/75 font-sans leading-relaxed max-w-lg">
                {renderTextWithBreaks(right.description)}
              </p>
            )}
          </div>

          {/* INTERACTIVE CARDS LIST (3 CARDS - COMPACT HEIGHT) */}
          {right.cards && right.cards.length > 0 && (
            <div className="space-y-2 sm:space-y-2.5 my-0.5">
              {right.cards.map((card) => {
                const isSelected = activeCardId === card.id;

                return (
                  <div
                    key={card.id}
                    onClick={() => handleCardClick(card.id)}
                    className={cn(
                      "p-3 sm:p-3.5 rounded-none transition-all duration-300 cursor-pointer flex flex-col justify-between border relative group",
                      isSelected
                        ? "bg-[#F1EDE2] text-primaryDark border-[#F1EDE2] shadow-xl scale-[1.01]"
                        : "bg-[#091D17]/70 hover:bg-primaryDark text-white border-white/15 hover:border-white/30 backdrop-blur-sm",
                    )}
                  >
                    <div className="flex items-start justify-between w-full">
                      <div className="space-y-0.5 pr-4">
                        <span
                          className={cn(
                            "text-[10px] sm:text-xs font-semibold uppercase tracking-wider block font-sans",
                            isSelected
                              ? "text-primaryDark/70"
                              : "text-white/60",
                          )}
                        >
                          {card.tag}
                        </span>

                        <h4
                          className={cn(
                            "text-sm sm:text-base font-lora font-normal leading-snug",
                            isSelected ? "text-primaryDark" : "text-white",
                          )}
                        >
                          {renderTextWithBreaks(card.title)}
                        </h4>
                      </div>

                      {/* ICON TOGGLE */}
                      <div className="pt-0.5">
                        {isSelected ? (
                          <Minus className="w-4 h-4 text-primaryDark flex-shrink-0" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    {/* SUBTEXT / TAGS LIST AT BOTTOM OF CARD */}
                    <p
                      className={cn(
                        "text-[10px] sm:text-xs font-sans font-light mt-1.5 pt-1 border-t",
                        isSelected
                          ? "text-primaryDark/70 border-primaryDark/15"
                          : "text-white/50 border-white/10",
                      )}
                    >
                      {card.subtext}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ================= RIGHT COLUMN: DYNAMIC MEDIA & INFO (65% WIDTH ON DESKTOP, SECOND ON MOBILE) ================= */}
      <div className="w-full lg:w-[65%] min-h-[auto] lg:h-full relative flex flex-col lg:block overflow-hidden bg-[#07251D] order-2 lg:order-2 flex-shrink-0">
        {/* MEDIA PREVIEW CONTAINER */}
        <div className="w-full aspect-[16/10] sm:aspect-[16/9] lg:aspect-none lg:h-full relative overflow-hidden bg-black/40 flex-shrink-0">
          {hasVideoUrl ? (
            isYouTube ? (
              <iframe
                ref={iframeRef}
                key={activeCard?.id || activeVideoUrl}
                src={getAutoplayUrl(activeVideoUrl)}
                title={activeCard?.title || "Video preview"}
                className="w-full h-full object-cover pointer-events-none scale-100 sm:scale-105 lg:scale-125 border-none"
                allow="autoplay; fullscreen"
              />
            ) : (
              <video
                ref={videoRef}
                key={activeCard?.id || activeVideoUrl}
                src={activeVideoUrl}
                poster={activeImage}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                className="w-full h-full object-cover object-center"
              />
            )
          ) : (
            <SmoothImage
              key={activeCard?.id || activeImage}
              src={activeImage}
              alt={
                activeCard?.title ||
                right?.title ||
                "Family & Wellness Dragon Home"
              }
              fill
              sizes="(max-width: 1024px) 100vw, 65vw"
              priority
              className="object-cover object-center"
            />
          )}

          {/* GRADIENT OVERLAY FOR TEXT CONTRAST AT BOTTOM (DESKTOP ONLY, HIDDEN ON VIDEO) */}
          {!hasVideoUrl && (
            <div className="hidden lg:block absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/85 via-black/35 to-transparent pointer-events-none z-10" />
          )}

          {/* VIDEO CONTROLS OVERLAY */}
          {hasVideoUrl && (
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-30 flex items-center gap-3 pointer-events-auto">
              {/* PLAY / PAUSE BUTTON */}
              <button
                type="button"
                onClick={togglePlay}
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] text-white flex items-center justify-center backdrop-blur-md shadow-lg transition-all transform hover:scale-105 active:scale-95 cursor-pointer group"
                aria-label={isPlaying ? "Dừng video" : "Phát video"}
                title={isPlaying ? "Dừng video" : "Phát video"}
              >
                {isPlaying ? (
                  <svg
                    className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors ml-0.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* VOLUME CONTROL BAR (ALWAYS VISIBLE SLIDER) */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#053328]/80 hover:bg-[#053328] border border-white/20 hover:border-[#E5CFB0] backdrop-blur-md shadow-lg transition-all">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="w-7 h-7 text-white flex items-center justify-center transition-all transform hover:scale-110 active:scale-95 cursor-pointer group"
                  aria-label={
                    isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"
                  }
                  title={isMuted || volume === 0 ? "Bật tiếng" : "Tắt tiếng"}
                >
                  {isMuted || volume === 0 ? (
                    <svg
                      className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                      viewBox="0 0 24 24"
                    >
                      <path d="M4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73 4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                    </svg>
                  ) : volume < 0.5 ? (
                    <svg
                      className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                      viewBox="0 0 24 24"
                    >
                      <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5 fill-current text-white group-hover:text-[#E5CFB0] transition-colors"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={(e) =>
                    handleVolumeChange(parseFloat(e.target.value))
                  }
                  className="w-20 sm:w-24 h-1.5 bg-white/30 rounded-lg appearance-none cursor-pointer accent-[#E5CFB0] hover:bg-white/40 transition-colors"
                  aria-label="Thanh điều chỉnh âm lượng"
                />
              </div>
            </div>
          )}
        </div>

        {/* BOTTOM CONTENT INFO: TAG, VIDEO, STATS & DISCLAIMER (SEPARATE BELOW MEDIA ON MOBILE/TABLET, OVERLAY AT BOTTOM ON DESKTOP) */}
        <div className="relative w-full p-6 sm:p-8 lg:p-10 z-20 flex flex-col justify-start lg:justify-end text-white bg-[#07251D] lg:bg-transparent lg:absolute lg:bottom-0 lg:inset-x-0">
          {activeMediaTag && (
            <p className="text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase text-[#EBDDBF] font-sans mb-3">
              {activeMediaTag}
            </p>
          )}

          {/* VIDEO PREVIEW PILL BADGE (ONLY DISPLAYED IF VIDEO URL EXISTS) */}
          {hasVideoUrl && activeVideoInfo && (
            <div
              onClick={onVideoClick}
              className="inline-flex items-center gap-3 bg-[#0D211A]/80 backdrop-blur-md border border-white/20 hover:border-[#EBDDBF] px-4 py-2.5 rounded-full transition-all cursor-pointer group max-w-max mb-5 shadow-lg"
            >
              <div className="w-7 h-7 rounded-full bg-[#EBDDBF] text-primaryDark flex items-center justify-center shadow group-hover:scale-105 transition-transform">
                <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
              </div>
              <div className="flex flex-col">
                <span className="text-[9px] font-mono tracking-widest text-white/60 uppercase leading-none mb-0.5">
                  XEM - {activeVideoInfo.duration}
                </span>
                <span className="text-xs font-medium text-white group-hover:text-[#EBDDBF] transition-colors leading-tight">
                  {activeVideoInfo.title}
                </span>
              </div>
            </div>
          )}

          {/* STATS COUNTER GRID */}
          {left.stats && left.stats.length > 0 && (
            <div className="pt-3.5 mb-2 border-t border-white/20 grid grid-cols-3 gap-3 sm:gap-6">
              {left.stats.map((stat, idx) => (
                <div key={idx} className="flex flex-col">
                  <span className="text-xl sm:text-2xl md:text-3xl font-lora font-normal text-white">
                    {stat.value}
                  </span>
                  <span className="text-[8px] sm:text-[9px] font-sans tracking-widest text-white/70 uppercase mt-0.5 leading-tight">
                    {stat.label}
                  </span>
                </div>
              ))}
            </div>
          )}

          {left.disclaimer && (
            <p className="text-[9px] sm:text-[10px] font-sans text-white/50 italic leading-tight">
              {left.disclaimer}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
